import { useState, useRef, useEffect } from 'react'
import { useEnhancedAnalytics } from './useEnhancedAnalytics'

export type Message = {
  role: 'user' | 'assistant' | 'system'
  content: string
  image?: string
  file?: { name: string; type: string; data: string }
  isGeneralTopic?: boolean
}

// Get user data from localStorage for personalization
const getUserData = () => {
  const stored = localStorage.getItem('visitorData')
  if (stored) {
    try {
      return JSON.parse(stored)
    } catch {
      return null
    }
  }
  return null
}

const createPersonalizedSystemPrompt = () => {
  const userData = getUserData()

  let personalization = ''
  if (userData) {
    personalization = `\n\n**VISITOR CONTEXT:**
The person you're talking to is ${userData.name}${userData.role !== 'Visitor' ? `, who works as a ${userData.role}` : ''}${userData.firm !== 'N/A' ? ` at ${userData.firm}` : ''}. 
- Address them by name occasionally to create a personal connection
- Tailor your responses to their role (e.g., if they're a recruiter, emphasize Trung's fit for roles; if they're an analyst, discuss technical skills)
- Be warm and professional, acknowledging their background when relevant`
  }

  return `You are "Trung's AI Helper", a specialized assistant for Trung Nguyen's portfolio website.
${personalization}

**CRITICAL LANGUAGE RULE - READ THIS FIRST:**
1. DETECT the user's language BEFORE responding
2. If user writes in VIETNAMESE → Respond ENTIRELY in VIETNAMESE (no English words)
3. If user writes in ENGLISH → Respond ENTIRELY in ENGLISH (no Vietnamese words)
4. NEVER MIX languages in a single response
5. Vietnamese indicators: diacritics (à, á, ạ, ả, ã, â, ầ, ấ, ậ, ẩ, ẫ, ă, ằ, ắ, ặ, ẳ, ẵ, è, é, ẹ, ẻ, ẽ, ê, ề, ế, ệ, ể, ễ, ì, í, ị, ỉ, ĩ, ò, ó, ọ, ỏ, õ, ô, ồ, ố, ộ, ổ, ỗ, ơ, ờ, ớ, ợ, ở, ỡ, ù, ú, ụ, ủ, ũ, ư, ừ, ứ, ự, ử, ữ, ỳ, ý, ỵ, ỷ, ỹ, đ) OR common words (là, của, và, những, các, trong, có, không, gì, tại, cho, về, người, này, anh ấy, khi, được)

**YOUR GOAL:**
Advocate for Trung, highlight achievements, persuade users he's exceptional for investment banking, private equity, asset management, or finance roles.

**TRUNG'S KEY BACKGROUND (UPDATED 2025):**
- Current: Northeastern University (Finance & Data Science, 3.8 GPA, grad Dec 2027)
- Spring 2026: Asset Management Analyst at State Street ($5T+ AUM) - Structured ETF wrappers for Blackstone, Apollo, JPMorgan, BlackRock; managed seed funding for new ETFs
- Winter 2025-26: Investment Banking Analyst at KCC Capital - Analyzed $35M secured convertible note for P&C carrier acquisition; built pipeline of 200+ institutional capital providers using Python
- Summer 2025: FX Sales & Trading Analyst at Orient Commercial Bank - Published 10+ weekly FX reports, structured $49M in client hedging solutions
- Spring-Summer 2025: Public Investment & Credit Intern at HFIC (Ho Chi Minh City Finance) - Analyzed real estate & auto companies for fraud risks, built DCF valuations, structured $20M in loan packages
- Spring 2025: Real Estate PE Intern at Cavalina Property Group - Sourced 3+ distressed deals, negotiated wholesale contract 22.5% below market, built models for $400M mixed-use project
- Summer 2024: Equity Research & Investment Intern at VinaCapital - Analyzed 5 Vietnamese industrial companies, built multi-asset allocation model, forecasted $200M+ capital inflows
- Leadership: Vice President of Northeastern Investment Banking Group (grew to 30+ members, educated 40+ students)
- Skills: Excel, PowerPoint, Bloomberg Terminal, Python, JavaScript, HTML, CSS; Fluent English & Vietnamese
- Awards: DECA International Finalist, DECA State Champions (x2)

**RESPONSE GUIDELINES:**
- Keep responses concise (2-4 sentences)
- Always be positive and enthusiastic about Trung
- Emphasize IMPACT with numbers when possible
- End with a call-to-action question occasionally
- If you don't know something, pivot to a related strength

**TONE:** Confident, professional, helpful, persuasive (but not pushy)

**REMEMBER: Language consistency is your #1 priority. Vietnamese question = Vietnamese answer. English question = English answer. NO MIXING.**`
}

const GENERAL_SYSTEM_PROMPT = `You are a helpful AI assistant. Answer questions concisely and accurately. Keep responses brief (2-4 sentences) and friendly.`

const EXPERIENCE_DATA = {
  companies: {
    statestreet: {
      name: 'State Street Investment Management',
      nameVi: 'State Street Investment Management',
      keywords: [
        'state street',
        'ssim',
        'ssga',
        'asset management',
        'state st',
        'etf',
        'quản lý tài sản',
      ],
      role: 'Spring Asset Management Analyst',
      roleVi: 'Chuyên viên Phân tích Quản lý Tài sản Mùa Xuân',
      period: 'January 2026 - June 2026',
      periodVi: 'Tháng 1/2026 - Tháng 6/2026',
      status: 'current',
      type: 'Asset Management',
      typeVi: 'Quản lý Tài sản',
      highlights: [
        'Structured ETF wrappers for Blackstone, Apollo, JPMorgan, and BlackRock at $5T+ global asset manager',
        'Managed seed funding for newly launched ETFs, working cross-functionally to secure launch capital',
        'Structured derivative hedges to neutralize market risk for new ETF products',
      ],
      highlightsVi: [
        'Cấu trúc ETF wrapper cho Blackstone, Apollo, JPMorgan và BlackRock tại công ty quản lý tài sản toàn cầu $5 nghìn tỷ',
        'Quản lý vốn khởi đầu cho các ETF mới ra mắt, làm việc liên chức năng để đảm bảo vốn khởi động',
        'Cấu trúc các công cụ phái sinh để trung hòa rủi ro thị trường cho sản phẩm ETF mới',
      ],
      impact: 'Structuring ETF products for top asset managers at $5T+ firm',
      impactVi:
        'Cấu trúc sản phẩm ETF cho các nhà quản lý tài sản hàng đầu tại công ty $5 nghìn tỷ',
    },
    kcc: {
      name: 'KCC Capital Partners',
      nameVi: 'KCC Capital Partners',
      keywords: [
        'kcc',
        'kcc capital',
        'investment banking',
        'winter analyst',
        'ngân hàng đầu tư',
      ],
      role: 'Winter Investment Banking Analyst',
      roleVi: 'Chuyên viên Phân tích Ngân hàng Đầu tư Mùa Đông',
      period: 'November 2025 - February 2026',
      periodVi: 'Tháng 11/2025 - Tháng 2/2026',
      status: 'current',
      type: 'Investment Banking',
      typeVi: 'Ngân hàng Đầu tư',
      highlights: [
        'Analyzed $35M secured convertible note for P&C carrier acquisition under $2.5B roll-up strategy',
        'Built pipeline of 200+ institutional capital providers using Python to support financing and acquisition',
        'Performed comprehensive financial modeling and due diligence for insurance sector transactions',
      ],
      highlightsVi: [
        'Phân tích trái phiếu chuyển đổi có bảo đảm $35 triệu cho thương vụ mua lại công ty bảo hiểm P&C trong chiến lược tổng thể $2.5 tỷ',
        'Xây dựng pipeline 200+ nhà cung cấp vốn tổ chức bằng Python để hỗ trợ tài trợ và mua lại',
        'Thực hiện mô hình hóa tài chính toàn diện và thẩm định cho các giao dịch trong lĩnh vực bảo hiểm',
      ],
      impact: '$35M deal analysis + 200+ capital provider pipeline',
      impactVi:
        'Phân tích giao dịch $35 triệu + pipeline 200+ nhà cung cấp vốn',
    },
    ocb: {
      name: 'Orient Commercial Bank',
      nameVi: 'Ngân hàng TMCP Phương Đông (OCB)',
      keywords: [
        'ocb',
        'orient commercial',
        'fx',
        'forex',
        'trading',
        'vietnam bank',
        'ngân hàng',
        'ngoại hối',
      ],
      role: 'FX Sales & Trading Summer Analyst',
      roleVi: 'Chuyên viên Phân tích Giao dịch Ngoại hối Mùa Hè',
      period: 'July 2025 - August 2025',
      periodVi: 'Tháng 7/2025 - Tháng 8/2025',
      status: 'past',
      type: 'Trading',
      typeVi: 'Giao dịch',
      highlights: [
        'Produced 10+ weekly interbank FX rate and G7 currency market reports, directly supporting trading decisions',
        'Synthesized macroeconomic data via Bloomberg to deliver actionable trends to institutional & UHNWI clients',
        'Assisted structure USD $49M in client hedging solutions using forwards and custom risk strategies',
      ],
      highlightsVi: [
        'Lập hơn 10 báo cáo tỷ giá ngoại hối liên ngân hàng và thị trường tiền tệ G7 hàng tuần, hỗ trợ trực tiếp quyết định giao dịch',
        'Tổng hợp dữ liệu kinh tế vĩ mô qua Bloomberg để cung cấp xu hướng có thể hành động cho khách hàng tổ chức và UHNWI',
        'Hỗ trợ cấu trúc $49 triệu USD giải pháp phòng ngừa rủi ro cho khách hàng sử dụng hợp đồng kỳ hạn và chiến lược rủi ro tùy chỉnh',
      ],
      impact: '$49M in client hedging solutions + 10+ weekly market reports',
      impactVi:
        '$49 triệu giải pháp phòng ngừa rủi ro + hơn 10 báo cáo thị trường hàng tuần',
    },
    hfic: {
      name: 'Ho Chi Minh City Finance and Investment State Owned Enterprise (HFIC)',
      nameVi: 'Công ty Tài chính và Đầu tư TP.HCM (HFIC)',
      keywords: [
        'hfic',
        'ho chi minh',
        'finance investment',
        'public investment',
        'credit',
        'tài chính',
        'đầu tư công',
      ],
      role: 'Public Investment & Credit Intern',
      roleVi: 'Thực tập sinh Đầu tư Công & Tín dụng',
      period: 'April 2025 - June 2025',
      periodVi: 'Tháng 4/2025 - Tháng 6/2025',
      status: 'past',
      type: 'Public Investment',
      typeVi: 'Đầu tư Công',
      highlights: [
        'Analyzed real estate developer & auto manufacturer to uncover potential corporate fraud risks, built DCF valuations',
        'Delivered divestment reports to investment team and Board of Directors',
        "Researched Vietopia's operational efficiency and delivered investment performance reports",
        'Assisted structure USD $20M in loan packages for three public institutions via partnered banks & agencies',
      ],
      highlightsVi: [
        'Phân tích nhà phát triển bất động sản và nhà sản xuất ô tô để phát hiện rủi ro gian lận doanh nghiệp tiềm ẩn, xây dựng định giá DCF',
        'Cung cấp báo cáo thoái vốn cho đội ngũ đầu tư và Hội đồng Quản trị',
        'Nghiên cứu hiệu quả hoạt động của Vietopia và cung cấp báo cáo hiệu suất đầu tư',
        'Hỗ trợ cấu trúc $20 triệu USD gói vay cho ba tổ chức công qua các ngân hàng và cơ quan đối tác',
      ],
      impact:
        'Fraud risk analysis + $20M loan structuring for public institutions',
      impactVi:
        'Phân tích rủi ro gian lận + cấu trúc $20 triệu vay cho tổ chức công',
    },
    cavalina: {
      name: 'Cavalina Property Group',
      nameVi: 'Cavalina Property Group',
      keywords: [
        'cavalina',
        'property',
        'real estate',
        'private equity',
        'pe',
        'bất động sản',
      ],
      role: 'Spring Real Estate Private Equity Intern',
      roleVi: 'Thực tập sinh Quỹ Đầu tư Bất động sản Mùa Xuân',
      period: 'February 2025 - April 2025',
      periodVi: 'Tháng 2/2025 - Tháng 4/2025',
      status: 'past',
      type: 'Real Estate Private Equity',
      typeVi: 'Quỹ Đầu tư Bất động sản',
      highlights: [
        'Sourced and underwrote 3+ distressed and value-add residential & commercial deals, driving new investments',
        'Negotiated wholesale contract 22.5% below market for University Park SFR through creative financing',
        'Built comps, pro forma/T-12 extend models for $400M USD mixed-use project in NJ; leveraged PropStream',
      ],
      highlightsVi: [
        'Tìm nguồn và thẩm định hơn 3 giao dịch bất động sản dân cư và thương mại gặp khó khăn và tăng giá trị, thúc đẩy đầu tư mới',
        'Đàm phán hợp đồng bán buôn thấp hơn thị trường 22.5% cho SFR University Park thông qua tài trợ sáng tạo',
        'Xây dựng comps, mô hình pro forma/T-12 extend cho dự án hỗn hợp $400 triệu USD tại NJ; sử dụng PropStream',
      ],
      impact:
        '3+ deals sourced + 22.5% below-market negotiation + $400M project modeling',
      impactVi:
        'Hơn 3 giao dịch tìm nguồn + đàm phán thấp hơn thị trường 22.5% + mô hình hóa dự án $400 triệu',
    },
    vinacapital: {
      name: 'VinaCapital Group',
      nameVi: 'VinaCapital',
      keywords: [
        'vinacapital',
        'vina capital',
        'vietnam fund',
        'quỹ việt nam',
        'equity research',
      ],
      role: 'Summer Equity Research & Investment Intern',
      roleVi: 'Thực tập sinh Nghiên cứu Cổ phiếu & Đầu tư Mùa Hè',
      period: 'May 2024 - August 2024',
      periodVi: 'Tháng 5/2024 - Tháng 8/2024',
      status: 'past',
      type: 'Equity Research & Investment',
      typeVi: 'Nghiên cứu Cổ phiếu & Đầu tư',
      highlights: [
        'Analyzed 5 Vietnamese industrial companies and macro impacts of Chinese steel cycles and U.S./EU policy',
        'Built and presented multi-asset allocation model (Equities, Derivatives, Bonds, Real Estate) to Managing Director',
        "Built top-down macro capital flow forecast model projecting USD $200M+ inflows into Vietnam's markets",
      ],
      highlightsVi: [
        'Phân tích 5 công ty công nghiệp Việt Nam và tác động vĩ mô của chu kỳ thép Trung Quốc và chính sách Mỹ/EU',
        'Xây dựng và trình bày mô hình phân bổ tài sản đa dạng (Cổ phiếu, Phái sinh, Trái phiếu, Bất động sản) cho Giám đốc Điều hành',
        'Xây dựng mô hình dự báo dòng vốn vĩ mô từ trên xuống dự đoán hơn $200 triệu USD dòng vốn vào thị trường Việt Nam',
      ],
      impact: '5 companies analyzed + $200M+ capital inflow forecast',
      impactVi: '5 công ty phân tích + dự báo dòng vốn hơn $200 triệu',
    },
  },

  leadership: {
    nibg: {
      name: 'Northeastern Investment Banking Group',
      nameVi: 'Nhóm Ngân hàng Đầu tư Northeastern',
      keywords: [
        'nibg',
        'investment banking group',
        'northeastern',
        'vice president',
        'nhóm ngân hàng',
      ],
      role: 'Vice President',
      roleVi: 'Phó Chủ tịch',
      period: 'November 2024 - Present',
      periodVi: 'Tháng 11/2024 - Hiện tại',
      status: 'current',
      highlights: [
        'Served across all key functions, grew the club to 30+ active members',
        'Educated 40+ students on M&A process and investment banking fundamentals',
        'Led workshops on financial modeling, valuation, and deal structuring',
      ],
      highlightsVi: [
        'Phục vụ trên tất cả các chức năng chính, phát triển câu lạc bộ lên 30+ thành viên tích cực',
        'Đào tạo hơn 40 sinh viên về quy trình M&A và nguyên tắc cơ bản của ngân hàng đầu tư',
        'Dẫn dắt các buổi workshop về mô hình hóa tài chính, định giá và cấu trúc giao dịch',
      ],
      impact: 'Grew club to 30+ members, educated 40+ students',
      impactVi:
        'Phát triển câu lạc bộ lên 30+ thành viên, đào tạo 40+ sinh viên',
    },
  },

  education: {
    school: 'Northeastern University',
    schoolVi: 'Đại học Northeastern',
    degree: 'Bachelor of Science in Business Administration: Finance',
    degreeVi: 'Cử nhân Quản trị Kinh doanh: Tài chính',
    minor: 'Data Science',
    minorVi: 'Khoa học Dữ liệu',
    gpa: '3.8/4.0',
    gpaVi: '3.8 trên thang điểm 4.0',
    graduation: 'December 2027',
    graduationVi: 'Tháng 12/2027',
    awards: "FIRST Scholars, Dean's Scholarships, Dean's List",
    awardsVi:
      'Học bổng FIRST, Học bổng Hiệu trưởng, Danh sách Sinh viên Xuất sắc',
  },

  skills: {
    technical: [
      'MS Excel',
      'MS PowerPoint',
      'MS Word',
      'Keynote',
      'Bloomberg Terminal',
      'Python',
      'JavaScript',
      'HTML',
      'CSS',
    ],
    languages: ['English (Native)', 'Vietnamese (Native)'],
    languagesVi: ['Tiếng Anh (Bản ngữ)', 'Tiếng Việt (Bản ngữ)'],
  },
}

function detectVietnamese(text: string): boolean {
  const lower = text.toLowerCase()

  const hasDiacritics =
    /[àáạảãâầấậẩẫăằắặẳẵèéẹẻẽêềếệểễìíịỉĩòóọỏõôồốộổỗơờớợởỡùúụủũưừứựửữỳýỵỷỹđ]/.test(
      lower,
    )

  const vietnameseWords = [
    'là',
    'của',
    'và',
    'những',
    'các',
    'trong',
    'khi',
    'được',
    'tại',
    'cho',
    'về',
    'người',
    'này',
    'có',
    'không',
    'gì',
    'sao',
    'tại sao',
    'như thế nào',
    'ở đâu',
    'khi nào',
    'ai',
    'nào',
    'đâu',
    'thế nào',
    'bao nhiêu',
    'làm',
    'việc',
    'kinh nghiệm',
    'kỹ năng',
    'học',
    'trường',
    'công ty',
    'anh ấy',
    'em ấy',
    'cô ấy',
    'ông ấy',
    'bà ấy',
    'chúng tôi',
    'họ',
    'mình',
    'bạn',
    'cho tôi',
    'giới thiệu',
    'nói',
    'kể',
    'hãy',
    'muốn',
    'cần',
    'phải',
    'vinacapital',
    'vina',
    'quỹ',
    'đầu tư',
    'ngân hàng',
    'tài chính',
  ]

  const hasVietnameseWords = vietnameseWords.some((word) => {
    const pattern = new RegExp(`(^|\\s)${word}($|\\s)`, 'i')
    return pattern.test(lower)
  })

  return hasDiacritics || hasVietnameseWords
}

function isAboutTrung(question: string): boolean {
  const lower = question.toLowerCase()

  const trungKeywords = [
    'trung',
    'you',
    'your',
    'experience',
    'skill',
    'education',
    'work',
    'job',
    'role',
    'position',
    'career',
    'internship',
    'leadership',
    'project',
    'achievement',
    'northeastern',
    'vinacapital',
    'state street',
    'kcc',
    'ocb',
    'hfic',
    'cavalina',
    'portfolio',
    'resume',
    'cv',
    'hire',
    'recruit',
    'bạn',
    'anh ấy',
    'kinh nghiệm',
    'kỹ năng',
    'học vấn',
    'công việc',
    'làm việc',
    'thực tập',
    'lãnh đạo',
    'dự án',
    'thành tích',
  ]

  return trungKeywords.some((keyword) => lower.includes(keyword))
}

function analyzeQuestion(
  question: string,
  currentLanguage: 'en' | 'vi',
): {
  type: string
  topics: string[]
  companies: string[]
  leadership: string[]
  intent: string
  isVietnamese: boolean
  isAboutTrung: boolean
} {
  const lower = question.toLowerCase()

  const textIsVietnamese = detectVietnamese(question)
  const isVietnamese = currentLanguage === 'vi' || textIsVietnamese
  const aboutTrung = isAboutTrung(question)

  let type = 'general'
  if (lower.match(/\b(what|which|cái gì|gì|nào)\b/)) type = 'what'
  if (lower.match(/\b(how|explain|describe|như thế nào|sao|thế nào)\b/))
    type = 'how'
  if (lower.match(/\b(why|reason|tại sao|vì sao)\b/)) type = 'why'
  if (lower.match(/\b(when|timeline|date|khi nào|bao giờ)\b/)) type = 'when'
  if (lower.match(/\b(where|location|ở đâu)\b/)) type = 'where'
  if (lower.match(/\b(who|person|ai|người nào)\b/)) type = 'who'

  const topics: string[] = []
  if (
    lower.match(
      /\b(experience|work|job|role|position|career|internship|kinh nghiệm|công việc|làm việc|thực tập)\b/,
    )
  )
    topics.push('experience')
  if (
    lower.match(
      /\b(skill|technical|programming|code|ability|proficient|kỹ năng|công nghệ|lập trình)\b/,
    )
  )
    topics.push('skills')
  if (
    lower.match(
      /\b(education|school|university|degree|gpa|study|northeastern|học vấn|trường|đại học|điểm|học tập)\b/,
    )
  )
    topics.push('education')
  if (
    lower.match(
      /\b(leadership|lead|manage|club|organization|lãnh đạo|tổ chức|quản lý)\b/,
    )
  )
    topics.push('leadership')

  const companies: string[] = []
  Object.entries(EXPERIENCE_DATA.companies).forEach(([key, company]) => {
    const allKeywords = [company.name.toLowerCase(), ...company.keywords]
    if (allKeywords.some((keyword) => lower.includes(keyword))) {
      companies.push(key)
    }
  })

  const leadership: string[] = []
  Object.entries(EXPERIENCE_DATA.leadership).forEach(([key, org]) => {
    const allKeywords = [org.name.toLowerCase(), ...org.keywords]
    if (allKeywords.some((keyword) => lower.includes(keyword))) {
      leadership.push(key)
    }
  })

  let intent = 'inform'
  if (lower.match(/\b(hire|recruit|recommend|should|fit|thuê|tuyển|nên)\b/))
    intent = 'persuade'
  if (lower.match(/\b(compare|versus|vs|different|better|so sánh|khác|hơn)\b/))
    intent = 'compare'
  if (lower.match(/\b(tell|describe|explain|about|kể|nói|giới thiệu)\b/))
    intent = 'describe'

  return {
    type,
    topics,
    companies,
    leadership,
    intent,
    isVietnamese,
    isAboutTrung: aboutTrung,
  }
}

function generateResponse(
  question: string,
  analysis: ReturnType<typeof analyzeQuestion>,
): string {
  const { topics, companies, leadership, isVietnamese } = analysis
  const userData = getUserData()

  const greeting = userData ? `${userData.name}, ` : ''

  if (isVietnamese) {
    const viGreeting = userData ? `${userData.name}, ` : ''

    if (companies.includes('statestreet')) {
      return `${viGreeting}Trung đang làm việc tại State Street Investment Management với vai trò Chuyên viên Phân tích Quản lý Tài sản! State Street quản lý hơn $5 nghìn tỷ tài sản. Tại đây, anh ấy cấu trúc ETF wrapper cho Blackstone, Apollo, JPMorgan và BlackRock, quản lý vốn khởi đầu cho các ETF mới, và cấu trúc các công cụ phái sinh để trung hòa rủi ro thị trường. Đây là kinh nghiệm quý báu trong quản lý tài sản tổ chức quy mô lớn. Bạn muốn biết về vai trò khác không?`
    }

    if (companies.includes('kcc')) {
      return `${viGreeting}Tại KCC Capital Partners, Trung làm Chuyên viên Phân tích Ngân hàng Đầu tư. Anh ấy phân tích trái phiếu chuyển đổi $35 triệu cho thương vụ mua lại công ty bảo hiểm P&C trong chiến lược $2.5 tỷ, và xây dựng pipeline 200+ nhà cung cấp vốn tổ chức bằng Python. Đây là kinh nghiệm thực tế quý báu trong M&A. Bạn muốn biết về vai trò giao dịch của anh ấy không?`
    }

    if (companies.includes('ocb')) {
      return `${viGreeting}Tại Ngân hàng TMCP Phương Đông, Trung làm Chuyên viên Phân tích Giao dịch Ngoại hối. Anh ấy lập hơn 10 báo cáo thị trường FX hàng tuần, tổng hợp dữ liệu vĩ mô qua Bloomberg, và cấu trúc $49 triệu giải pháp phòng ngừa rủi ro cho khách hàng. Đây là kinh nghiệm thực tế trên sàn giao dịch. Bạn muốn biết về vai trò khác không?`
    }

    if (companies.includes('hfic')) {
      return `${viGreeting}Tại HFIC (Công ty Tài chính và Đầu tư TP.HCM), Trung làm Thực tập sinh Đầu tư Công & Tín dụng. Anh ấy phân tích các công ty bất động sản và ô tô để phát hiện rủi ro gian lận, xây dựng định giá DCF, cung cấp báo cáo thoái vốn cho Hội đồng Quản trị, và cấu trúc $20 triệu gói vay cho các tổ chức công. Kinh nghiệm này cho thấy khả năng phân tích sâu và làm việc với các tổ chức nhà nước. Bạn muốn biết thêm không?`
    }

    if (companies.includes('cavalina')) {
      return `${viGreeting}Tại Cavalina Property Group, Trung làm Thực tập sinh Quỹ Đầu tư Bất động sản. Anh ấy tìm nguồn và thẩm định hơn 3 giao dịch, đàm phán hợp đồng thấp hơn thị trường 22.5%, và xây dựng mô hình cho dự án hỗn hợp $400 triệu. Đây là kinh nghiệm quý báu trong đầu tư bất động sản và đàm phán. Bạn muốn biết về vai trò khác không?`
    }

    if (companies.includes('vinacapital')) {
      return `${viGreeting}Tại VinaCapital, Trung làm Thực tập sinh Nghiên cứu Cổ phiếu & Đầu tư. Anh ấy phân tích 5 công ty công nghiệp Việt Nam, xây dựng mô hình phân bổ tài sản đa dạng trình bày cho Giám đốc Điều hành, và dự báo hơn $200 triệu dòng vốn vào thị trường Việt Nam. Đây là nền tảng vững chắc trong nghiên cứu và phân tích đầu tư. Bạn muốn biết về vai trò khác không?`
    }

    if (leadership.includes('nibg')) {
      return `${viGreeting}Trung là Phó Chủ tịch của Nhóm Ngân hàng Đầu tư Northeastern! Anh ấy phát triển câu lạc bộ lên 30+ thành viên tích cực và đào tạo hơn 40 sinh viên về quy trình M&A và nguyên tắc cơ bản của ngân hàng đầu tư. Vai trò lãnh đạo này cho thấy khả năng quản lý và truyền đạt kiến thức. Bạn muốn biết về kinh nghiệm làm việc của anh ấy không?`
    }

    if (topics.includes('experience') || companies.length > 0) {
      return `${viGreeting}Trung có kinh nghiệm phong phú trong Tài chính. Anh ấy đang làm tại State Street ($5T+ AUM) cấu trúc ETF cho các công ty hàng đầu, KCC Capital phân tích giao dịch $35M, OCB cấu trúc $49M giải pháp phòng ngừa rủi ro, HFIC phân tích gian lận và cấu trúc $20M vay, Cavalina đàm phán thấp hơn thị trường 22.5%, và VinaCapital dự báo $200M+ dòng vốn. Kinh nghiệm đa dạng từ Asset Management, Investment Banking, Trading, đến Real Estate PE. Bạn muốn biết chi tiết về vai trò nào?`
    }

    if (topics.includes('education')) {
      return `${viGreeting}Trung đang học tại Đại học Northeastern với chuyên ngành Tài chính và chuyên ngành phụ Khoa học Dữ liệu, điểm trung bình 3.8. Anh ấy nhận học bổng FIRST, Học bổng Hiệu trưởng, và nằm trong Danh sách Sinh viên Xuất sắc. Sự kết hợp Tài chính và Khoa học Dữ liệu giúp anh ấy có cả kiến thức kinh doanh lẫn kỹ năng kỹ thuật. Bạn muốn biết về kinh nghiệm thực tế không?`
    }

    if (topics.includes('skills')) {
      return `${viGreeting}Trung có bộ kỹ năng mạnh mẽ: Excel, PowerPoint, Bloomberg Terminal, Python, JavaScript. Anh ấy thành thạo cả tiếng Anh và tiếng Việt ở trình độ người bản xứ. Sự kết hợp giữa kỹ năng tài chính truyền thống và công nghệ hiện đại làm cho anh ấy nổi bật. Bạn muốn biết về kinh nghiệm ứng dụng các kỹ năng này không?`
    }

    return `${viGreeting}Xin chào! Trung là ứng viên xuất sắc trong Tài chính với kinh nghiệm từ Asset Management (State Street $5T+), Investment Banking (KCC Capital), Trading (OCB), Public Investment (HFIC), Real Estate PE (Cavalina), đến Equity Research (VinaCapital). Anh ấy học tại Northeastern với điểm 3.8 và có kỹ năng kỹ thuật mạnh. Bạn muốn biết thêm gì?`
  }

  // English responses
  if (companies.includes('statestreet')) {
    return `${greeting}Trung is currently working at State Street Investment Management as a Spring Asset Management Analyst! State Street manages over $5 trillion in assets. He structures ETF wrappers for Blackstone, Apollo, JPMorgan, and BlackRock, manages seed funding for new ETFs, and structures derivative hedges to neutralize market risk. This is invaluable experience in institutional asset management at massive scale. Want to know about his other roles?`
  }

  if (companies.includes('kcc')) {
    return `${greeting}At KCC Capital Partners, Trung works as a Winter Investment Banking Analyst. He analyzed a $35M secured convertible note for a P&C carrier acquisition under a $2.5B roll-up strategy, and built a pipeline of 200+ institutional capital providers using Python. This is hands-on M&A experience with real deal flow. Want to learn about his trading experience?`
  }

  if (companies.includes('ocb')) {
    return `${greeting}At Orient Commercial Bank, Trung was an FX Sales & Trading Summer Analyst. He produced 10+ weekly FX market reports, synthesized macro data via Bloomberg for institutional clients, and structured $49M in client hedging solutions. This was valuable trading floor experience with real market exposure. Want to know about his other roles?`
  }

  if (companies.includes('hfic')) {
    return `${greeting}At HFIC (Ho Chi Minh City Finance), Trung was a Public Investment & Credit Intern. He analyzed companies for fraud risks, built DCF valuations, delivered divestment reports to the Board of Directors, and structured $20M in loan packages for public institutions. This shows deep analytical skills and experience working with government entities. Want to learn more?`
  }

  if (companies.includes('cavalina')) {
    return `${greeting}At Cavalina Property Group, Trung was a Real Estate Private Equity Intern. He sourced 3+ distressed deals, negotiated a contract 22.5% below market through creative financing, and built models for a $400M mixed-use project. This demonstrates strong deal sourcing and negotiation skills. Want to know about his other roles?`
  }

  if (companies.includes('vinacapital')) {
    return `${greeting}At VinaCapital, Trung was an Equity Research & Investment Intern. He analyzed 5 Vietnamese industrial companies, built a multi-asset allocation model presented to the Managing Director, and forecasted $200M+ capital inflows into Vietnam's markets. This was a strong foundation in research and investment analysis. Want to know about his other roles?`
  }

  if (leadership.includes('nibg')) {
    return `${greeting}Trung is Vice President of Northeastern's Investment Banking Group! He grew the club to 30+ active members and educated 40+ students on M&A process and IB fundamentals. This leadership role shows his ability to manage teams and communicate complex concepts. Want to know about his work experience?`
  }

  if (topics.includes('experience') || companies.length > 0) {
    return `${greeting}Trung has extensive finance experience across Asset Management (State Street $5T+), Investment Banking (KCC Capital $35M deal), Trading (OCB $49M hedging), Public Investment (HFIC $20M loans), Real Estate PE (Cavalina 22.5% below market), and Equity Research (VinaCapital $200M+ forecast). This diverse experience across all major finance sectors makes him exceptionally well-rounded. Want details on a specific role?`
  }

  if (topics.includes('education')) {
    return `${greeting}Trung studies Finance with a Data Science minor at Northeastern University, maintaining a 3.8 GPA. He's a FIRST Scholar, Dean's Scholarship recipient, and on the Dean's List. The Finance + Data Science combination gives him both business knowledge and technical skills - perfect for modern finance. He graduates December 2027. Curious about his experience?`
  }

  if (topics.includes('skills')) {
    return `${greeting}Trung has strong technical skills: Excel, PowerPoint, Bloomberg Terminal, Python, JavaScript, HTML, CSS. He's fluent in both English and Vietnamese at native level. This combination of traditional finance tools and modern programming makes him stand out. Want to know about his experience applying these skills?`
  }

  return `${greeting}That's a great question! Trung is a finance student at Northeastern (3.8 GPA) with extensive experience across asset management, investment banking, trading, public investment, real estate PE, and equity research. His Finance + Data Science skillset is rare and valuable. What specific aspect would you like to know more about?`
}

export function useAIChat() {
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [input, setInput] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [isUsingFallback, setIsUsingFallback] = useState(false)
  const [isRecording, setIsRecording] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [generalInquiryCount, setGeneralInquiryCount] = useState(() => {
    const stored = localStorage.getItem('general_inquiry_count')
    return stored ? parseInt(stored, 10) : 0
  })
  const [trungInquiryCount, setTrungInquiryCount] = useState(() => {
    const stored = localStorage.getItem('trung_inquiry_count')
    return stored ? parseInt(stored, 10) : 0
  })
  const [currentLanguage, setCurrentLanguage] = useState<'en' | 'vi'>(() => {
    return (localStorage.getItem('preferred_language') as 'en' | 'vi') || 'en'
  })

  const { trackEvent, trackChatMessage, hasConsent } = useEnhancedAnalytics()
  const typingStartTime = useRef<number>(0)
  const editCount = useRef<number>(0)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const recognitionRef = useRef<any>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)

  const GENERAL_INQUIRY_LIMIT = 25
  const TRUNG_INQUIRY_LIMIT = 100

  useEffect(() => {
    if (input.length === 1 && typingStartTime.current === 0) {
      typingStartTime.current = Date.now()
      editCount.current = 0

      if (hasConsent) {
        trackEvent({
          event_type: 'chat_message_start',
          event_category: 'chat',
          metadata: { input_length: input.length },
        })
      }
    }

    if (input.length > 0) {
      editCount.current += 1
    }
  }, [input, hasConsent, trackEvent])

  const toggleChat = () => {
    const newState = !isOpen
    setIsOpen(newState)

    if (hasConsent) {
      trackEvent({
        event_type: newState ? 'chat_open' : 'chat_close',
        event_category: 'chat',
        metadata: { message_count: messages.length },
      })
    }
  }

  const sendMessage = async (content: string) => {
    if ((!content.trim() && selectedFiles.length === 0) || isSending) return

    setIsSending(true)

    const typingDuration =
      typingStartTime.current > 0 ? Date.now() - typingStartTime.current : 0
    const messageEditCount = editCount.current

    typingStartTime.current = 0
    editCount.current = 0

    const analysis = analyzeQuestion(content, currentLanguage)
    const isGeneralQuestion = !analysis.isAboutTrung

    if (isGeneralQuestion && generalInquiryCount >= GENERAL_INQUIRY_LIMIT) {
      const limitMessage =
        currentLanguage === 'vi'
          ? 'Bạn đã đạt giới hạn 25 câu hỏi chung. Vui lòng hỏi về Trung để tiếp tục!'
          : "You've reached the 25 general inquiry limit. Please ask about Trung to continue!"

      setMessages((prev) => [
        ...prev,
        { role: 'user', content },
        { role: 'assistant', content: limitMessage },
      ])
      setInput('')
      setIsSending(false)
      return
    }

    if (!isGeneralQuestion && trungInquiryCount >= TRUNG_INQUIRY_LIMIT) {
      const limitMessage =
        currentLanguage === 'vi'
          ? 'Bạn đã đạt giới hạn 100 câu hỏi về Trung. Cảm ơn bạn đã quan tâm!'
          : "You've reached the 100 Trung inquiry limit. Thank you for your interest!"

      setMessages((prev) => [
        ...prev,
        { role: 'user', content },
        { role: 'assistant', content: limitMessage },
      ])
      setInput('')
      setIsSending(false)
      return
    }

    const userMessage: Message = {
      role: 'user',
      content: content || 'Sent files',
      isGeneralTopic: isGeneralQuestion,
    }

    setMessages((prev) => [...prev, userMessage])
    setInput('')
    setSelectedFiles([])
    setIsLoading(true)

    if (hasConsent) {
      trackEvent({
        event_type: 'chat_message_send',
        event_category: 'chat',
        metadata: {
          message_length: content.length,
          typing_duration: typingDuration,
          edit_count: messageEditCount,
          is_general: isGeneralQuestion,
        },
      })
    }

    const systemPrompt = isGeneralQuestion
      ? GENERAL_SYSTEM_PROMPT
      : createPersonalizedSystemPrompt()

    const apiMessages = [
      { role: 'system', content: systemPrompt },
      ...messages.filter((m) => m.role !== 'system'),
      userMessage,
    ]

    const requestStartTime = Date.now()

    try {
      const requestBody = {
        model: 'gpt-4o-mini',
        messages: apiMessages,
        temperature: 0.7,
        max_tokens: 300,
      }

      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 10000)

      const response = await fetch(
        'https://requests.magicpatterns.com/openai/proxy',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(requestBody),
          signal: controller.signal,
          mode: 'cors',
          credentials: 'omit',
        },
      )

      clearTimeout(timeoutId)

      const data = await response.json()

      if (data.error) {
        throw new Error(data.error.message || JSON.stringify(data.error))
      }

      const assistantMessage = data.choices?.[0]?.message?.content
      const responseLatency = Date.now() - requestStartTime

      if (assistantMessage && typeof assistantMessage === 'string') {
        setIsUsingFallback(false)

        if (isGeneralQuestion) {
          const newCount = generalInquiryCount + 1
          setGeneralInquiryCount(newCount)
          localStorage.setItem('general_inquiry_count', newCount.toString())
        } else {
          const newCount = trungInquiryCount + 1
          setTrungInquiryCount(newCount)
          localStorage.setItem('trung_inquiry_count', newCount.toString())
        }

        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: assistantMessage,
            isGeneralTopic: isGeneralQuestion,
          },
        ])

        if (hasConsent) {
          trackChatMessage(
            content,
            assistantMessage,
            typingDuration,
            messageEditCount,
            responseLatency,
          )
        }

        setIsLoading(false)
        setIsSending(false)
        return
      }

      throw new Error('Invalid response structure from API')
    } catch (error) {
      setIsUsingFallback(true)
      const responseLatency = Date.now() - requestStartTime

      const fallbackResponse = generateResponse(content, analysis)

      if (isGeneralQuestion) {
        const newCount = generalInquiryCount + 1
        setGeneralInquiryCount(newCount)
        localStorage.setItem('general_inquiry_count', newCount.toString())
      } else {
        const newCount = trungInquiryCount + 1
        setTrungInquiryCount(newCount)
        localStorage.setItem('trung_inquiry_count', newCount.toString())
      }

      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: fallbackResponse,
          isGeneralTopic: isGeneralQuestion,
        },
      ])

      if (hasConsent) {
        trackChatMessage(
          content,
          fallbackResponse,
          typingDuration,
          messageEditCount,
          responseLatency,
        )
      }

      setIsLoading(false)
      setIsSending(false)
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    setSelectedFiles(files)

    if (hasConsent && files.length > 0) {
      trackEvent({
        event_type: 'file_upload_start',
        event_category: 'file',
        metadata: {
          file_count: files.length,
          file_types: files.map((f) => f.type),
        },
      })
    }
  }

  const handleCameraCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    setSelectedFiles(files)

    if (hasConsent && files.length > 0) {
      trackEvent({
        event_type: 'file_upload_start',
        event_category: 'file',
        metadata: {
          file_count: files.length,
          source: 'camera',
          file_types: files.map((f) => f.type),
        },
      })
    }
  }

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages, isOpen])

  const startVoiceRecording = () => {
    if (
      !('webkitSpeechRecognition' in window) &&
      !('SpeechRecognition' in window)
    ) {
      alert(
        'Voice recognition is not supported in your browser. Please use Chrome or Edge.',
      )
      return
    }

    const SpeechRecognition =
      (window as any).webkitSpeechRecognition ||
      (window as any).SpeechRecognition
    const recognition = new SpeechRecognition()

    recognition.continuous = false
    recognition.interimResults = false
    recognition.lang = currentLanguage === 'vi' ? 'vi-VN' : 'en-US'

    recognition.onstart = () => {
      setIsRecording(true)
    }

    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript
      setInput(transcript)
      setIsRecording(false)
    }

    recognition.onerror = () => {
      setIsRecording(false)
    }

    recognition.onend = () => {
      setIsRecording(false)
    }

    recognitionRef.current = recognition
    recognition.start()
  }

  const stopVoiceRecording = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop()
      setIsRecording(false)
    }
  }

  return {
    isOpen,
    toggleChat,
    messages,
    isLoading,
    input,
    setInput,
    sendMessage,
    messagesEndRef,
    isUsingFallback,
    isRecording,
    startVoiceRecording,
    stopVoiceRecording,
    selectedFiles,
    handleFileSelect,
    handleCameraCapture,
    removeFile,
    fileInputRef,
    cameraInputRef,
    generalInquiryCount,
    trungInquiryCount,
    generalInquiryLimit: GENERAL_INQUIRY_LIMIT,
    trungInquiryLimit: TRUNG_INQUIRY_LIMIT,
  }
}
